import { addMonths, endOfMonth, format, getDaysInMonth, parse, setDate, startOfDay, subDays, subMonths } from 'date-fns';

/**
 * Calcula as datas de abertura, fechamento e vencimento de uma fatura a partir do
 * mês de referência (YYYY-MM) e dos dias de fechamento/vencimento do cartão.
 *
 * Lógica pura (date-fns) — vive aqui, em lib/, pra ser a ÚNICA fonte da janela do ciclo:
 * a tela do cartão (getInvoiceCycleDates antigo) e o backfill no boot (initDatabase)
 * reutilizam esta função em vez de reimplementar a regra da borda, que já causou o bug de
 * sobreposição. Ver docs/RECURRING_CREDIT_CARD_TROUBLESHOOTING.md.
 *
 * `closingDayToNextInvoice` (ADR 0130, ajuste por cartão): a compra do PRÓPRIO dia do
 * fechamento vai pra fatura seguinte, como fazem alguns bancos. A janela então termina no dia
 * ANTERIOR ao fechamento cadastrado e a seguinte abre no próprio dia — `getInvoiceMonthForDate`
 * usa a mesma borda (`>=`). O vencimento não muda: `dueDay < closingDay` segue olhando o dia
 * cadastrado, que é o que o banco imprime.
 */
export function calculateInvoiceDates(
    referenceMonth: string,
    closingDay: number,
    dueDay: number,
    closingDayToNextInvoice = false,
): { openingDate: string; closingDate: string; dueDate: string } {
    const refDate = parse(referenceMonth + '-01', 'yyyy-MM-dd', new Date());
    const prevMonth = subMonths(refDate, 1);
    const prevMonthLastDay = endOfMonth(prevMonth).getDate();
    const refMonthLastDay = endOfMonth(refDate).getDate();

    if (closingDayToNextInvoice) {
        // Abre no dia do fechamento do mês anterior (clampado) e fecha na véspera do
        // fechamento deste mês: contíguo com a anterior, que fechou na véspera do seu. Com
        // fechamento dia 1 a janela é o mês anterior inteiro.
        const openingDate = setDate(prevMonth, Math.min(closingDay, prevMonthLastDay));
        const closingDate = subDays(setDate(refDate, Math.min(closingDay, refMonthLastDay)), 1);
        return {
            openingDate: format(startOfDay(openingDate), 'yyyy-MM-dd'),
            closingDate: format(startOfDay(closingDate), 'yyyy-MM-dd'),
            dueDate: format(startOfDay(resolveDueDate(refDate, closingDay, dueDay)), 'yyyy-MM-dd'),
        };
    }

    // Abertura = dia seguinte ao fechamento do mês anterior.
    // Se closingDay+1 passa do último dia do mês anterior, a fatura anterior fechou no
    // ÚLTIMO dia dele (fechamento clampado) — então esta abre no 1º dia do mês de
    // referência, NÃO no último dia do anterior. Usar endOfMonth(prevMonth) aqui fazia a
    // janela desta fatura sobrepor a anterior em 1 dia (ex.: fecha 30 → fev fecha 28 e
    // mar abria 28), e uma ocorrência nesse dia (clampada ou dia 28) duplicava nas duas
    // faturas. Ver docs/RECURRING_CREDIT_CARD_TROUBLESHOOTING.md (achado nº2, borda de fev).
    const openingDay = closingDay + 1;
    let openingDate: Date;
    if (openingDay > prevMonthLastDay) {
        openingDate = refDate; // 1º dia do mês de referência (dia seguinte ao fechamento do mês anterior)
    } else {
        openingDate = setDate(prevMonth, openingDay);
    }

    // Fechamento = dia de fechamento do mês de referência (clampado ao tamanho do mês)
    const actualClosingDay = Math.min(closingDay, refMonthLastDay);
    const closingDate = setDate(refDate, actualClosingDay);

    return {
        openingDate: format(startOfDay(openingDate), 'yyyy-MM-dd'),
        closingDate: format(startOfDay(closingDate), 'yyyy-MM-dd'),
        dueDate: format(startOfDay(resolveDueDate(refDate, closingDay, dueDay)), 'yyyy-MM-dd'),
    };
}

/** Vencimento = dia de vencimento do mês de referência (ou do mês seguinte se dueDay < closingDay). */
function resolveDueDate(refDate: Date, closingDay: number, dueDay: number): Date {
    let dueDateMonth = refDate;
    if (dueDay < closingDay) {
        dueDateMonth = addMonths(refDate, 1);
    }
    const dueMonthLastDay = endOfMonth(dueDateMonth).getDate();
    const actualDueDay = Math.min(dueDay, dueMonthLastDay);
    return setDate(dueDateMonth, actualDueDay);
}

/**
 * Mês em que a fatura é NOMEADA para o usuário (`yyyy-MM`): o do **vencimento nominal**,
 * como os bancos nomeiam. É outro conceito que o `referenceMonth`, que identifica a fatura
 * e ancora no fechamento — nos cartões com `dueDay < closingDay` (fecha 27, vence 10) os
 * dois divergem em um mês. Ver ADR 0102.
 *
 * Recebe o `dueDate` GRAVADO da fatura (nominal). Nunca passar o vencimento útil: o
 * rolamento por dia útil pode cruzar a virada do mês e renomearia a fatura (ADR 0082).
 */
export function getInvoiceDisplayMonth(dueDate: string): string {
    return dueDate.slice(0, 7);
}

/**
 * O mesmo nome para uma fatura que ainda não existe como linha (mês projetado no
 * carrossel da tela do cartão, alvo do stepper do formulário): deriva o vencimento do mês
 * de referência pela MESMA regra do ciclo, em vez de reimplementar o "+1 mês".
 */
export function getInvoiceDisplayMonthForReference(
    referenceMonth: string,
    closingDay: number,
    dueDay: number,
    closingDayToNextInvoice = false,
): string {
    return getInvoiceDisplayMonth(
        calculateInvoiceDates(referenceMonth, closingDay, dueDay, closingDayToNextInvoice).dueDate,
    );
}

/**
 * Em qual fatura (mês de referência) uma compra feita em `purchaseDate` cai. A compra do
 * próprio dia do fechamento fica na fatura que fecha nele — salvo `closingDayToNextInvoice`
 * (ADR 0130), quando ela já vai pra seguinte, como no banco do Brenno.
 */
export function getInvoiceMonthForDate(
    purchaseDate: string,
    closingDay: number,
    closingDayToNextInvoice = false,
): string {
    const date = parse(purchaseDate, 'yyyy-MM-dd', new Date());
    const day = date.getDate();

    // Clampa o dia de fechamento ao tamanho do mês (ex.: fechamento 31 em fevereiro = 28),
    // espelhando calculateInvoiceDates em invoiceRepository. Na prática isto é defensivo:
    // como `day` nunca passa de getDaysInMonth, a comparação já se resolve igual sem o clamp.
    // Mas manter as duas funções com a MESMA noção de "dia efetivo de fechamento" evita
    // divergência futura caso a regra de borda mude. Ver docs/RECURRING_CREDIT_CARD_TROUBLESHOOTING.md.
    const effectiveClosingDay = Math.min(closingDay, getDaysInMonth(date));

    if (day > effectiveClosingDay || (closingDayToNextInvoice && day === effectiveClosingDay)) {
        return format(addMonths(date, 1), 'yyyy-MM');
    }

    return format(date, 'yyyy-MM');
}
